import { Section, Callout } from '@/components/content/ContentBits';

const INDIA_STACK = [
  { offer: 'Google AI Pro × Jio', what: '18 months free (₹1,950/mo value) for eligible Jio users', claim: "MyJio app — check every teammate's SIM" },
  { offer: 'Perplexity Pro × Airtel', what: '12 months free', claim: 'Airtel Thanks app' },
  { offer: 'ChatGPT Go India', what: '₹399/mo plan, free for 12 months (active promo)', claim: 'In-app — verify promo still open when claiming' },
  { offer: 'Google AI Plus', what: '₹199/mo first 6 months, then ₹399', claim: 'One tier, shareable with 5 family members' },
];

const CHECKS = [
  { label: 'Nonprofit status', body: "you're recognized as a charitable/nonprofit organization by the regulator in your country of registration. For Indian orgs: your registration as a Trust, Society, or Section 8 Company is exactly this." },
  { label: 'Not-for-profit operation & public benefit', body: 'what your registration documents already establish.' },
  { label: 'Applicant association', body: 'proof that you actually belong to the org. Goodstack does this primarily by domain matching: your application email\u2019s domain should match your organization\u2019s website domain. If it doesn\u2019t (e.g., you applied from a personal Gmail), they contact the organization to confirm you — slower, but it works.' },
  { label: 'Good standing', body: 'your org is in compliance with its regulator.' },
];

const HOW_TO = [
  "Go to Claude's nonprofit page (claude.com — Claude for Nonprofits) and start the application; it routes you into Goodstack's verification flow.",
  'Apply using your org-domain email if you have one.',
  'Upload/enter your registration details when asked.',
  'Wait for the confirmation email; then your workspace billing can move to the nonprofit rate when the sponsored period ends.',
  "Stuck at any step? Goodstack runs its own applicant support — use the help option inside the verification flow, or ask your Claude workspace to explain the step you're on (it's rather good at that).",
];

const StackSections = () => (
  <>
    {/* Your workspace */}
    <Section title="Your workspace: what you have and until when">
      <p className="text-ink leading-relaxed">
        Your organization has a <strong>Claude Team workspace — 5 seats, valid 6 months</strong>{' '}
        (until ~February 2027), courtesy of Anthropic via Basecamp. Everything from today is saved
        inside it. Three rules: seats belong to your <strong>organization</strong> (org emails, not
        personal Gmail); your <strong>tool steward</strong> owns invites and renewals; before month six
        ends, your org makes one decision per tool — free tier, India offer, or nonprofit rate. The rest
        of this page makes that decision easy.
      </p>
    </Section>

    {/* Goodstack */}
    <Section title="Goodstack: verify once, unlock the nonprofit internet">
      <div className="space-y-5 text-ink leading-relaxed">
        <p>
          <strong>What it is.</strong> Goodstack (formerly Percent) is the verification infrastructure
          the world's biggest tech companies use to confirm that an organization is a genuine nonprofit.
          Its database covers 9 million+ organizations across 240+ countries — and it's the engine behind
          Google for Nonprofits, Anthropic's Claude for Nonprofits, and nonprofit programs from companies
          like Canva, monday.com, and Asana.
        </p>
        <p>
          <strong>Why it matters to you.</strong> Companies want to give nonprofits discounted or free
          tools, but they need proof an org is real. Goodstack is that proof — and once you're verified
          through one partner program, the same verification identity opens others. For this room, the
          headline door it opens: <strong>Claude Team at ~$8 (≈₹700) per user per month — up to 75% off</strong>{' '}
          — the plan your sponsored workspace runs on today. Verifying now means your February decision is
          a small renewal, not a cliff.
        </p>

        <Callout variant="orange">
          <p>
            <strong>Does it cost money? No.</strong> Verification through a partner program costs your
            organization nothing — Goodstack charges the companies running the programs, not the causes
            applying to them. If anyone ever asks your nonprofit to pay for Goodstack verification, that's
            a red flag, not a fee.
          </p>
        </Callout>

        <div>
          <p className="font-bold mb-3">What the verification checks. Four things, per Goodstack's published process:</p>
          <ol className="space-y-3">
            {CHECKS.map((c, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-teal/10 text-brand-teal text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-[15px]"><strong>{c.label}</strong> — {c.body}</span>
              </li>
            ))}
          </ol>
        </div>

        <Callout label="What to keep handy (Indian orgs)" variant="teal">
          <p>
            Registration certificate (Trust deed / Society registration / Section 8 incorporation), PAN,
            12A and 80G certificates (not always required, but they strengthen and speed everything), your
            website URL, and an <strong>org-domain email address</strong> for the applicant. Note: each
            partner (Anthropic, Google, etc.) sets its own eligibility rules on top of the base
            verification — so approval into one program doesn't automatically guarantee another, but the
            heavy lifting carries over.
          </p>
        </Callout>

        <p>
          <strong>How long it takes.</strong> Goodstack advertises verification in hours-to-days, not
          weeks — the practical variable is the applicant-association check, which is near-instant with a
          matching org domain and slower via manual confirmation without one.
        </p>

        <div>
          <p className="font-bold mb-3">How to actually do it (the path for this room):</p>
          <ol className="space-y-2.5">
            {HOW_TO.map((s, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="w-6 h-6 rounded-full bg-brand-teal text-white text-xs font-bold flex items-center justify-center shrink-0 mt-0.5">{i + 1}</span>
                <span className="text-[15px]">{s}</span>
              </li>
            ))}
          </ol>
        </div>

        <p>
          <strong>One honest caveat:</strong> Claude for Nonprofits covers registered charitable
          organizations and their international equivalents — Indian Trusts/Societies/Section 8 companies
          fit that definition, but final eligibility is the partner's call at verification time. That's
          precisely why your steward should start the process <em>now</em>, five months before the
          sponsored period ends — early enough that any hiccup has time to resolve.
        </p>
        <p className="text-ink-secondary italic text-sm">
          (No org-domain email? That's worth fixing regardless — Google Workspace for Nonprofits gives
          your org professional email on your own domain, free… unlocked, fittingly, by the same Goodstack
          verification.)
        </p>
      </div>
    </Section>

    {/* India stack table */}
    <Section title="The ₹0–₹399 India stack (claim this week)">
      <div className="rounded-2xl bg-white border border-black/[0.07] shadow-sm overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-ink-muted border-b border-black/[0.06]">
              <th className="px-5 py-3 font-semibold">Offer</th>
              <th className="px-5 py-3 font-semibold">What</th>
              <th className="px-5 py-3 font-semibold">Claim</th>
            </tr>
          </thead>
          <tbody>
            {INDIA_STACK.map((row, i) => (
              <tr key={i} className="border-t border-black/[0.05] first:border-0 align-top">
                <td className="px-5 py-3 font-semibold text-ink">{row.offer}</td>
                <td className="px-5 py-3 text-ink-secondary">{row.what}</td>
                <td className="px-5 py-3 text-ink-secondary">{row.claim}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-ink leading-relaxed mt-5">
        Free-forever floor beneath all of it: Claude free tier · Gemini free tier · Google AI Studio ·
        script.google.com. Your prompts and skills are portable — the R-T-C-F framework works in every one
        of these tools.
      </p>
      <Callout label="Rule before paying for anything" variant="orange">
        <p className="text-lg font-semibold">
          A free tier must block you three times in one week. Until then, you don't need the upgrade.
        </p>
      </Callout>
    </Section>

    {/* 48-hour challenge */}
    <Section title="The 48-hour challenge">
      <Callout variant="teal">
        <p>
          Within two days: pick one real task, apply R-T-C-F, save the prompt to your library. Ten minutes
          saved this week becomes an hour saved every week by next month. That's the whole game.
        </p>
      </Callout>
    </Section>
  </>
);

export default StackSections;
